import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Autocomplete,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  SwapHoriz as TransferIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Save as SaveIcon,
  LocationOn as LocationIcon,
  Receipt as DocumentIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Remove as RemoveIcon,
  LocalShipping as ShippingIcon,
  Store as StoreIcon,
  Warehouse as WarehouseIcon,
  Inventory as InventoryIcon,
  ArrowForward as ArrowIcon,
} from "@mui/icons-material";

const StockTransfer = () => {
  // Sample locations data
  const locations = [
    { id: 1, code: "LOC001", name: "Main Warehouse - Mumbai", type: "Warehouse" },
    { id: 2, code: "LOC002", name: "Retail Store - Andheri", type: "Store" },
    { id: 3, code: "LOC003", name: "Retail Store - Bandra", type: "Store" },
    { id: 4, code: "LOC004", name: "Distribution Center - Pune", type: "Warehouse" },
    { id: 5, code: "LOC005", name: "Retail Store - Delhi CP", type: "Store" },
  ];

  // Sample products data with location-wise stock
  const products = [
    { 
      id: 1, 
      code: "PRD001", 
      name: "Samsung Galaxy S23", 
      category: "Electronics", 
      unit: "Piece",
      stockByLocation: {
        1: 100, // Main Warehouse
        2: 15,  // Andheri
        3: 20,  // Bandra
        4: 50,  // Pune
        5: 10,  // Delhi
      }
    },
    { 
      id: 2, 
      code: "PRD002", 
      name: "Apple iPhone 14", 
      category: "Electronics", 
      unit: "Piece",
      stockByLocation: {
        1: 80,
        2: 10,
        3: 12,
        4: 40,
        5: 8,
      }
    },
    { 
      id: 3, 
      code: "PRD003", 
      name: "Sony LED TV 55\"", 
      category: "Electronics", 
      unit: "Piece",
      stockByLocation: {
        1: 50,
        2: 5,
        3: 8,
        4: 20,
        5: 3,
      }
    },
    { 
      id: 4, 
      code: "PRD004", 
      name: "HP Laptop i5", 
      category: "Electronics", 
      unit: "Piece",
      stockByLocation: {
        1: 60,
        2: 8,
        3: 10,
        4: 25,
        5: 5,
      }
    },
    { 
      id: 5, 
      code: "PRD005", 
      name: "Nike Sports Shoes", 
      category: "Clothing", 
      unit: "Pair",
      stockByLocation: {
        1: 200,
        2: 30,
        3: 40,
        4: 80,
        5: 20,
      }
    },
  ];

  // Transfer entries data
  const [transferData, setTransferData] = useState([
    {
      id: 1,
      transferNo: "TRF-2024-001",
      transferDate: "2024-01-15",
      fromLocation: "Main Warehouse - Mumbai",
      toLocation: "Retail Store - Andheri",
      totalItems: 3,
      totalQuantity: 25,
      expectedDelivery: "2024-01-17",
      vehicleNo: "MH-01-AB-1234",
      driverName: "Rajesh Kumar",
      driverPhone: "+91-9876543210",
      status: "Completed",
      createdDate: "2024-01-15",
    },
    {
      id: 2,
      transferNo: "TRF-2024-002",
      transferDate: "2024-01-18",
      fromLocation: "Main Warehouse - Mumbai",
      toLocation: "Distribution Center - Pune",
      totalItems: 5,
      totalQuantity: 50,
      expectedDelivery: "2024-01-20",
      vehicleNo: "MH-02-CD-5678",
      driverName: "Amit Patel",
      driverPhone: "+91-9876543211",
      status: "In Transit",
      createdDate: "2024-01-18",
    },
    {
      id: 3,
      transferNo: "TRF-2024-003",
      transferDate: "2024-01-20",
      fromLocation: "Retail Store - Bandra",
      toLocation: "Retail Store - Andheri",
      totalItems: 2,
      totalQuantity: 10,
      expectedDelivery: "2024-01-21",
      vehicleNo: "MH-01-EF-9012",
      driverName: "Suresh Sharma",
      driverPhone: "+91-9876543212",
      status: "Pending",
      createdDate: "2024-01-20",
    },
  ]);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState({});

  // Modal state
  const [openModal, setOpenModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [editingTransfer, setEditingTransfer] = useState(null);
  const [viewingTransfer, setViewingTransfer] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Transfer form state
  const [transferForm, setTransferForm] = useState({
    transferNo: "",
    transferDate: new Date().toISOString().split('T')[0],
    fromLocationId: null,
    toLocationId: null,
    expectedDelivery: "",
    vehicleNo: "",
    driverName: "",
    driverPhone: "",
    transporterName: "",
    trackingNo: "",
    remarks: "",
    status: "Pending",
  });

  // Transfer items state
  const [transferItems, setTransferItems] = useState([
    {
      id: Date.now(),
      productId: null,
      productName: "",
      availableStock: 0,
      quantity: 1,
    }
  ]);

  // Helper functions
  const getFilteredTransfers = () => {
    return transferData.filter((transfer) => {
      const matchesSearch = Object.values(transfer).some((value) =>
        String(value).toLowerCase().includes(searchQuery.toLowerCase())
      );
      const matchesStatus = statusFilter === "all" || transfer.status === statusFilter;
      const matchesLocation = locationFilter === "all" || 
        transfer.fromLocation.includes(locationFilter) || 
        transfer.toLocation.includes(locationFilter);
      return matchesSearch && matchesStatus && matchesLocation;
    });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Completed':
        return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
      case 'In Transit':
        return { backgroundColor: '#e3f2fd', color: '#1976d2' };
      case 'Pending':
        return { backgroundColor: '#fff3e0', color: '#e65100' };
      case 'Cancelled':
        return { backgroundColor: '#ffebee', color: '#d32f2f' };
      default:
        return { backgroundColor: '#f5f5f5', color: '#666' };
    }
  };

  const getLocationIcon = (type) => {
    return type === "Warehouse" ? <WarehouseIcon /> : <StoreIcon />;
  };

  // Handlers
  const handleEdit = (transfer) => {
    setEditingTransfer(transfer);
    setOpenModal(true);
  };

  const handleView = (transfer) => {
    setViewingTransfer(transfer);
    setOpenViewModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this transfer entry?")) {
      setTransferData(transferData.filter((transfer) => transfer.id !== id));
      setSuccessMessage("Transfer entry deleted successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
    setSuccessMessage(`Search completed. Found ${getFilteredTransfers().length} entries.`);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleReset = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setLocationFilter("all");
    setPage(0);
    setErrors({});
    setSuccessMessage("");
  };

  const handleOpenModal = () => {
    setEditingTransfer(null);
    const newTransferNo = `TRF-${new Date().getFullYear()}-${String(transferData.length + 1).padStart(3, '0')}`;
    setTransferForm({
      transferNo: newTransferNo,
      transferDate: new Date().toISOString().split('T')[0],
      fromLocationId: null,
      toLocationId: null,
      expectedDelivery: "",
      vehicleNo: "",
      driverName: "",
      driverPhone: "",
      transporterName: "",
      trackingNo: "",
      remarks: "",
      status: "Pending",
    });
    setTransferItems([
      {
        id: Date.now(),
        productId: null,
        productName: "",
        availableStock: 0,
        quantity: 1,
      }
    ]);
    setErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingTransfer(null);
    setErrors({});
  };

  const handleCloseViewModal = () => {
    setOpenViewModal(false);
    setViewingTransfer(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setTransferForm({ ...transferForm, [name]: value });
  };

  const handleFromLocationChange = (event, newValue) => {
    setTransferForm({ 
      ...transferForm, 
      fromLocationId: newValue ? newValue.id : null 
    });
    // Reset items when from location changes
    setTransferItems([
      {
        id: Date.now(),
        productId: null,
        productName: "",
        availableStock: 0,
        quantity: 1,
      }
    ]);
  };

  const handleToLocationChange = (event, newValue) => {
    setTransferForm({ 
      ...transferForm, 
      toLocationId: newValue ? newValue.id : null 
    });
  };

  const handleProductChange = (index, event, newValue) => {
    const updatedItems = [...transferItems];
    if (newValue && transferForm.fromLocationId) {
      const availStock = newValue.stockByLocation[transferForm.fromLocationId] || 0;
      updatedItems[index] = {
        ...updatedItems[index],
        productId: newValue.id,
        productName: newValue.name,
        availableStock: availStock,
      };
    } else {
      updatedItems[index] = {
        ...updatedItems[index],
        productId: null,
        productName: "",
        availableStock: 0,
      };
    }
    setTransferItems(updatedItems);
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...transferItems];
    updatedItems[index][field] = parseInt(value) || 0;
    
    // Validate quantity against available stock
    if (field === 'quantity' && updatedItems[index].quantity > updatedItems[index].availableStock) {
      setErrors({ 
        ...errors, 
        [`item_${index}`]: `Only ${updatedItems[index].availableStock} units available at source location` 
      });
    } else {
      const newErrors = { ...errors };
      delete newErrors[`item_${index}`];
      setErrors(newErrors);
    }
    
    setTransferItems(updatedItems);
  };

  const handleAddItem = () => {
    setTransferItems([
      ...transferItems,
      {
        id: Date.now(),
        productId: null,
        productName: "",
        availableStock: 0,
        quantity: 1,
      }
    ]);
  };

  const handleRemoveItem = (index) => {
    if (transferItems.length > 1) {
      const updatedItems = transferItems.filter((_, i) => i !== index);
      setTransferItems(updatedItems);
    }
  };

  const handleSaveTransfer = async (isDraft = false) => {
    // Validation
    if (!transferForm.fromLocationId) {
      setErrors({ fromLocation: "Please select source location" });
      return;
    }
    if (!transferForm.toLocationId) {
      setErrors({ toLocation: "Please select destination location" });
      return;
    }
    if (transferForm.fromLocationId === transferForm.toLocationId) {
      setErrors({ toLocation: "Source and destination locations must be different" });
      return;
    }
    const validItems = transferItems.filter(item => item.productId);
    if (validItems.length === 0) {
      setErrors({ items: "Please add at least one product" });
      return;
    }
    // Check stock availability
    for (let i = 0; i < validItems.length; i++) {
      if (validItems[i].quantity > validItems[i].availableStock) {
        setErrors({ items: `Insufficient stock for ${validItems[i].productName}` });
        return;
      }
    }

    setIsLoading(true);
    try {
      const totalQuantity = validItems.reduce((sum, item) => sum + item.quantity, 0);
      const fromLoc = locations.find(l => l.id === transferForm.fromLocationId);
      const toLoc = locations.find(l => l.id === transferForm.toLocationId);

      const newTransfer = {
        id: transferData.length + 1,
        transferNo: transferForm.transferNo,
        transferDate: transferForm.transferDate,
        fromLocation: fromLoc.name,
        toLocation: toLoc.name,
        totalItems: validItems.length,
        totalQuantity: totalQuantity,
        expectedDelivery: transferForm.expectedDelivery,
        vehicleNo: transferForm.vehicleNo,
        driverName: transferForm.driverName,
        driverPhone: transferForm.driverPhone,
        status: isDraft ? "Pending" : transferForm.status,
        createdDate: new Date().toISOString().split('T')[0],
      };

      setTransferData([newTransfer, ...transferData]);
      setSuccessMessage(`Transfer ${isDraft ? 'saved as pending' : 'initiated'} successfully!`);
      handleCloseModal();
    } catch (error) {
      setErrors({ general: "An error occurred. Please try again." });
    } finally {
      setIsLoading(false);
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  const filteredTransfers = getFilteredTransfers();

  // Calculate totals
  const totalQuantity = transferItems.reduce((sum, item) => 
    item.productId ? sum + item.quantity : sum, 0
  );
  const totalItems = transferItems.filter(item => item.productId).length;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TransferIcon sx={{ color: 'primary.main', fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" color="primary">
              Stock Transfer Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Transfer inventory between locations and warehouses • {filteredTransfers.length} transfers found
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenModal}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 600,
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
        >
          New Transfer
        </Button>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Search and Filter Section */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search Transfers..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2.5}>
            <TextField
              select
              fullWidth
              label="Location"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              SelectProps={{
                native: true,
              }}
            >
              <option value="all">All Locations</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.name}>{loc.name}</option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2.5}>
            <TextField
              select
              fullWidth
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              SelectProps={{
                native: true,
              }}
            >
              <option value="all">All Status</option>
              <option value="Pending">Pending</option>
              <option value="In Transit">In Transit</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
                sx={{ flex: 1 }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<RefreshIcon />}
                sx={{ flex: 1 }}
              >
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Table Section */}
      <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>S.No</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Transfer No</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>From → To</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Items/Qty</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Expected Delivery</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Vehicle Details</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTransfers.length > 0 ? (
                filteredTransfers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((transfer, index) => (
                    <TableRow 
                      key={transfer.id} 
                      sx={{ 
                        '&:hover': { backgroundColor: '#f5f5f5' },
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600" sx={{ fontFamily: 'monospace' }}>
                          {transfer.transferNo}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {transfer.transferDate}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                            <Typography variant="caption" fontWeight="600">
                              {transfer.fromLocation}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ArrowIcon sx={{ fontSize: 14 }} />
                            <LocationIcon sx={{ fontSize: 14, color: 'success.main' }} />
                            <Typography variant="caption" fontWeight="600">
                              {transfer.toLocation}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="600">
                            {transfer.totalItems} items
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {transfer.totalQuantity} qty
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {transfer.expectedDelivery}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="caption" fontWeight="600">
                            {transfer.vehicleNo}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            {transfer.driverName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={transfer.status}
                          size="small"
                          sx={{
                            ...getStatusStyle(transfer.status),
                            fontWeight: 600,
                            fontSize: '0.75rem'
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleView(transfer)}
                              sx={{
                                color: 'info.main',
                                '&:hover': { backgroundColor: 'info.light' },
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(transfer)}
                              sx={{
                                color: 'warning.main',
                                '&:hover': { backgroundColor: 'warning.light' },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(transfer.id)}
                              sx={{
                                color: 'error.main',
                                '&:hover': { backgroundColor: 'error.light' },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <TransferIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      No transfer entries found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredTransfers.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Transfer Entry Modal */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="xl" fullWidth>
        <DialogTitle sx={{
          backgroundColor: 'primary.main',
          color: 'white',
          padding: '16px 24px',
        }}>
          <Typography variant="h6" component="div" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <TransferIcon />
            {editingTransfer ? 'Edit Transfer' : 'New Stock Transfer'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: '24px', mt: 2 }}>
          {/* Transfer Header Information */}
          <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f8f9fa' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
              Transfer Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Transfer No"
                  name="transferNo"
                  value={transferForm.transferNo}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DocumentIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Transfer Date"
                  name="transferDate"
                  type="date"
                  value={transferForm.transferDate}
                  onChange={handleFormChange}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Expected Delivery Date"
                  name="expectedDelivery"
                  type="date"
                  value={transferForm.expectedDelivery}
                  onChange={handleFormChange}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Location Selection */}
          <Paper sx={{ p: 3, mb: 3, backgroundColor: '#e3f2fd' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
              Transfer Route
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <Autocomplete
                  options={locations}
                  getOptionLabel={(option) => `${option.code} - ${option.name}`}
                  onChange={handleFromLocationChange}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getLocationIcon(option.type)}
                        <Box>
                          <Typography variant="body2">{option.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{option.code}</Typography>
                        </Box>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="From Location (Source) *"
                      error={!!errors.fromLocation}
                      helperText={errors.fromLocation}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <LocationIcon color="primary" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={2} sx={{ textAlign: 'center' }}>
                <TransferIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              </Grid>
              <Grid item xs={12} md={5}>
                <Autocomplete
                  options={locations.filter(l => l.id !== transferForm.fromLocationId)}
                  getOptionLabel={(option) => `${option.code} - ${option.name}`}
                  onChange={handleToLocationChange}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getLocationIcon(option.type)}
                        <Box>
                          <Typography variant="body2">{option.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{option.code}</Typography>
                        </Box>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="To Location (Destination) *"
                      error={!!errors.toLocation}
                      helperText={errors.toLocation}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <LocationIcon color="success" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Product Items Table */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold" color="primary">
                Product Items to Transfer
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddItem}
                size="small"
                disabled={!transferForm.fromLocationId}
              >
                Add Item
              </Button>
            </Box>
            {!transferForm.fromLocationId && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Please select source location first to add products
              </Alert>
            )}
            {errors.items && (
              <Alert severity="error" sx={{ mb: 2 }}>{errors.items}</Alert>
            )}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 'bold', width: '5%' }}>S.No</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>Available at Source</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>Transfer Quantity</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '15%' }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transferItems.map((item, index) => (
                    <TableRow key={item.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Autocomplete
                          options={products}
                          getOptionLabel={(option) => `${option.code} - ${option.name}`}
                          onChange={(event, newValue) => handleProductChange(index, event, newValue)}
                          disabled={!transferForm.fromLocationId}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select Product"
                              size="small"
                              fullWidth
                            />
                          )}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`${item.availableStock} units`}
                          size="small"
                          color={item.availableStock < 10 ? "error" : item.availableStock < 50 ? "warning" : "success"}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          inputProps={{ min: 1, max: item.availableStock, step: 1 }}
                          fullWidth
                          error={!!errors[`item_${index}`]}
                          helperText={errors[`item_${index}`]}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveItem(index)}
                          disabled={transferItems.length === 1}
                          color="error"
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Transport Details and Summary */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                  Transport & Logistics Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Vehicle Number"
                      name="vehicleNo"
                      value={transferForm.vehicleNo}
                      onChange={handleFormChange}
                      placeholder="e.g., MH-01-AB-1234"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ShippingIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Transporter Name"
                      name="transporterName"
                      value={transferForm.transporterName}
                      onChange={handleFormChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Driver Name"
                      name="driverName"
                      value={transferForm.driverName}
                      onChange={handleFormChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Driver Phone"
                      name="driverPhone"
                      value={transferForm.driverPhone}
                      onChange={handleFormChange}
                      placeholder="+91-9876543210"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Tracking Number / Way Bill"
                      name="trackingNo"
                      value={transferForm.trackingNo}
                      onChange={handleFormChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Remarks / Notes"
                      name="remarks"
                      value={transferForm.remarks}
                      onChange={handleFormChange}
                      multiline
                      rows={2}
                      placeholder="Any special instructions or notes..."
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 3, backgroundColor: '#f8f9fa', height: '100%' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                  Transfer Summary
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1">Total Items:</Typography>
                    <Typography variant="body1" fontWeight="600">
                      {totalItems}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1">Total Quantity:</Typography>
                    <Typography variant="body1" fontWeight="600">
                      {totalQuantity} units
                    </Typography>
                  </Box>
                  <Divider />
                  {transferForm.fromLocationId && transferForm.toLocationId && (
                    <Box sx={{ mt: 2 }}>
                      <Alert severity="info" icon={<TransferIcon />}>
                        <Typography variant="body2" fontWeight="bold">
                          Transfer Route:
                        </Typography>
                        <Typography variant="caption" display="block">
                          From: {locations.find(l => l.id === transferForm.fromLocationId)?.name}
                        </Typography>
                        <Typography variant="caption" display="block">
                          To: {locations.find(l => l.id === transferForm.toLocationId)?.name}
                        </Typography>
                      </Alert>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {errors.general && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errors.general}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
          <Button onClick={handleCloseModal} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleSaveTransfer(true)}
            disabled={isLoading}
            startIcon={<SaveIcon />}
          >
            Save as Pending
          </Button>
          <Button
            variant="contained"
            onClick={() => handleSaveTransfer(false)}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            {isLoading ? 'Processing...' : 'Initiate Transfer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Transfer Modal */}
      <Dialog open={openViewModal} onClose={handleCloseViewModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          backgroundColor: 'primary.main',
          color: 'white',
          padding: '16px 24px',
        }}>
          <Typography variant="h6" component="div" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon />
            Transfer Details
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: '24px', mt: 2 }}>
          {viewingTransfer && (
            <Grid container spacing={3}>
              {/* Progress Stepper */}
              <Grid item xs={12}>
                <Stepper activeStep={
                  viewingTransfer.status === "Pending" ? 0 :
                  viewingTransfer.status === "In Transit" ? 1 :
                  viewingTransfer.status === "Completed" ? 2 : 0
                } alternativeLabel>
                  <Step>
                    <StepLabel>Pending</StepLabel>
                  </Step>
                  <Step>
                    <StepLabel>In Transit</StepLabel>
                  </Step>
                  <Step>
                    <StepLabel>Completed</StepLabel>
                  </Step>
                </Stepper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Transfer No</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1, fontFamily: 'monospace' }}>
                    {viewingTransfer.transferNo}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Transfer Date</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                    {viewingTransfer.transferDate}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">From Location</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <LocationIcon color="primary" />
                    <Typography variant="body1" fontWeight="bold">
                      {viewingTransfer.fromLocation}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">To Location</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <LocationIcon color="success" />
                    <Typography variant="body1" fontWeight="bold">
                      {viewingTransfer.toLocation}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Total Items / Quantity</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                    {viewingTransfer.totalItems} items / {viewingTransfer.totalQuantity} qty
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Expected Delivery</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                    {viewingTransfer.expectedDelivery}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Vehicle Number</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1, fontFamily: 'monospace' }}>
                    {viewingTransfer.vehicleNo}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(83, 109, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Driver Details</Typography>
                  <Typography variant="body2" fontWeight="bold" sx={{ mt: 1 }}>
                    {viewingTransfer.driverName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {viewingTransfer.driverPhone}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: 'rgba(144, 19, 254, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={viewingTransfer.status}
                      sx={{
                        ...getStatusStyle(viewingTransfer.status),
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button onClick={handleCloseViewModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StockTransfer;

